import { useAppDispatch } from "@/hooks/redux";
import { useSelectedCameraStream } from "@/modules/camera-selection";
import { useEffect, useRef } from "react";
import { bumpPipeline } from "../services/cameraPipelineGenerationSlice";

export interface UseCameraStreamRecoveryOptions {
  /** Camera reachable (cameras query resolved with the selected camera). */
  isCameraAvailable: boolean;
  /** Cameras query is on its first load — don't arm the recovery yet. */
  isLoading: boolean;
}

/**
 * Rebuilds the shared WebRTC connection when the camera becomes available
 * again after being unavailable (empty list OR network error).
 *
 * CameraStreamProvider retries a dead connection by itself, but backs off to
 * one attempt every 30 s while the camera stays away. Bumping the generation
 * retries the moment the camera is listed again, not whenever the next attempt
 * happens to be due.
 *
 * The isLoading guard keeps the initial load (undefined → data) from
 * mistakenly counting as a recovery.
 */
export const useCameraStreamRecovery = ({
  isCameraAvailable,
  isLoading,
}: UseCameraStreamRecoveryOptions) => {
  const dispatch = useAppDispatch();
  const { cameraMxid, streamName } = useSelectedCameraStream();
  const wasUnavailableRef = useRef(false);

  useEffect(() => {
    if (isLoading) return;
    if (!isCameraAvailable) {
      wasUnavailableRef.current = true;
      return;
    }
    if (wasUnavailableRef.current && cameraMxid && streamName) {
      wasUnavailableRef.current = false;
      dispatch(bumpPipeline({ mxid: cameraMxid, streamName }));
    }
  }, [isCameraAvailable, isLoading, cameraMxid, streamName, dispatch]);
};
